/**
 * Desktop (VM) Snapshot Redux Slice
 *
 * Manages state for EBS-backed DesktopSnapshot resources. This is DISTINCT
 * from the legacy data/slices/snapshot.js slice which handles container
 * Session snapshots — they target different CRDs and different endpoints.
 *
 * Endpoints used (see linuxApi.js):
 *   GET    /linux/{id}/snapshots
 *   POST   /linux/{id}/snapshots
 *   GET    /linux/snapshots
 *   DELETE /linux/snapshots/{name}
 *   POST   /linux  (with body.restore.fromSnapshot=<name>)
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  listDesktopSnapshots,
  createDesktopSnapshot,
  deleteDesktopSnapshot,
  restoreDesktopFromSnapshot
} from "../api/linuxApi";

export const fetchDesktopSnapshots = createAsyncThunk(
  "desktopSnapshots/fetch",
  async (desktopId, { rejectWithValue }) => {
    try {
      const data = await listDesktopSnapshots(desktopId);
      return { desktopId, items: data.items || [] };
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const createDesktopSnapshotThunk = createAsyncThunk(
  "desktopSnapshots/create",
  async ({ desktopId, displayName, description, consistency, visibility }, { rejectWithValue }) => {
    try {
      const data = await createDesktopSnapshot(desktopId, {
        displayName,
        description,
        consistency,
        visibility
      });
      return { desktopId, snapshot: data };
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const deleteDesktopSnapshotThunk = createAsyncThunk(
  "desktopSnapshots/delete",
  async ({ name, desktopId }, { rejectWithValue }) => {
    try {
      await deleteDesktopSnapshot(name);
      return { name, desktopId };
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const restoreDesktopFromSnapshotThunk = createAsyncThunk(
  "desktopSnapshots/restore",
  async ({ name, params }, { rejectWithValue }) => {
    try {
      const data = await restoreDesktopFromSnapshot(name, params || {});
      return data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

const slice = createSlice({
  name: "desktopSnapshots",
  initialState: {
    // byDesktop[desktopId] = { items: [], loading, error, lastFetched }
    byDesktop: {},
    creating: false,
    deleting: false,
    restoring: false,
    actionError: null
  },
  reducers: {
    clearSnapshotError(state) {
      state.actionError = null;
    }
  },
  extraReducers: (b) => {
    b.addCase(fetchDesktopSnapshots.pending, (state, action) => {
      const id = action.meta.arg;
      state.byDesktop[id] = { ...(state.byDesktop[id] || {}), loading: true, error: null };
    });
    b.addCase(fetchDesktopSnapshots.fulfilled, (state, action) => {
      const { desktopId, items } = action.payload;
      state.byDesktop[desktopId] = {
        items,
        loading: false,
        error: null,
        lastFetched: Date.now()
      };
    });
    b.addCase(fetchDesktopSnapshots.rejected, (state, action) => {
      const id = action.meta.arg;
      state.byDesktop[id] = {
        ...(state.byDesktop[id] || { items: [] }),
        loading: false,
        error: action.payload || action.error.message
      };
    });

    b.addCase(createDesktopSnapshotThunk.pending, (state) => {
      state.creating = true;
      state.actionError = null;
    });
    b.addCase(createDesktopSnapshotThunk.fulfilled, (state, action) => {
      state.creating = false;
      const { desktopId, snapshot } = action.payload;
      const bucket = state.byDesktop[desktopId] || { items: [] };
      // Prepend the new snapshot so users see it immediately.
      bucket.items = [snapshot, ...(bucket.items || [])];
      state.byDesktop[desktopId] = bucket;
    });
    b.addCase(createDesktopSnapshotThunk.rejected, (state, action) => {
      state.creating = false;
      state.actionError = action.payload || action.error.message;
    });

    b.addCase(deleteDesktopSnapshotThunk.pending, (state) => {
      state.deleting = true;
      state.actionError = null;
    });
    b.addCase(deleteDesktopSnapshotThunk.fulfilled, (state, action) => {
      state.deleting = false;
      const { name, desktopId } = action.payload;
      if (desktopId && state.byDesktop[desktopId]) {
        state.byDesktop[desktopId].items = (state.byDesktop[desktopId].items || []).filter(
          (s) => s.id !== name
        );
      }
    });
    b.addCase(deleteDesktopSnapshotThunk.rejected, (state, action) => {
      state.deleting = false;
      state.actionError = action.payload || action.error.message;
    });

    b.addCase(restoreDesktopFromSnapshotThunk.pending, (state) => {
      state.restoring = true;
      state.actionError = null;
    });
    b.addCase(restoreDesktopFromSnapshotThunk.fulfilled, (state) => {
      state.restoring = false;
    });
    b.addCase(restoreDesktopFromSnapshotThunk.rejected, (state, action) => {
      state.restoring = false;
      state.actionError = action.payload || action.error.message;
    });
  }
});

export const { clearSnapshotError } = slice.actions;
export default slice.reducer;
